import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Badge
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Science as ResearchIcon,
  Code as CodeIcon,
  AutoAwesome as MagicIcon
} from '@mui/icons-material';

interface QueueStatusProps {
  status: any;
  onRefresh: () => void;
  loading: boolean;
}

const QueueStatus: React.FC<QueueStatusProps> = ({ 
  status, 
  onRefresh,
  loading 
}) => {
  const [messages, setMessages] = useState<Array<{ id: string; text: string; severity: 'success' | 'error' }>>([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [previousCompletions, setPreviousCompletions] = useState<string[]>([]);
  const [previousResearchCompletions, setPreviousResearchCompletions] = useState<string[]>([]);

  // Auto-refresh every 5 seconds when there's active processing
  useEffect(() => {
    if (!autoRefresh) return;
    
    const hasActiveProcessing = 
      status?.stats?.schemaGeneration?.processing > 0 || 
      status?.stats?.research?.processing > 0;
    
    if (hasActiveProcessing) {
      const interval = setInterval(onRefresh, 5000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, status, onRefresh]);

  // Check for new completions and show notifications
  useEffect(() => {
    if (!status?.recentCompletions || status.recentCompletions.length === 0) return;
    
    const currentCompletionIds = status.recentCompletions.map((c: any) => c.id);
    const newCompletions = status.recentCompletions.filter(
      (completion: any) => !previousCompletions.includes(completion.id)
    );
    
    if (newCompletions.length > 0) {
      // Show notification for each new completion
      newCompletions.forEach((completion: any) => {
        const urlName = completion.url
          .replace(/^https?:\/\//, '')
          .replace(/^www\./, '')
          .replace(/\/$/, '')
          .split('/')
          .pop()
          ?.replace(/-/g, ' ')
          .replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Unknown';
        
        const newMessage = {
          id: `${completion.id}-${Date.now()}`,
          text: `🎉 Schema completed for ${urlName} (${completion.completeness_score}% complete)`,
          severity: 'success' as const
        };
        
        setMessages(prev => [...prev, newMessage]);
        
        // Auto-remove message after 6 seconds
        setTimeout(() => {
          setMessages(prev => prev.filter(msg => msg.id !== newMessage.id));
        }, 6000);
      });
    }
    
    setPreviousCompletions(currentCompletionIds);
  }, [status?.recentCompletions, previousCompletions]);

  // Check for new research completions
  useEffect(() => {
    if (!status?.recentResearchCompletions || status.recentResearchCompletions.length === 0) return;
    
    const currentResearchIds = status.recentResearchCompletions.map((c: any) => c.id);
    const newResearchCompletions = status.recentResearchCompletions.filter(
      (completion: any) => !previousResearchCompletions.includes(completion.id)
    );
    
    if (newResearchCompletions.length > 0) {
      newResearchCompletions.forEach((completion: any) => {
        const urlName = completion.url
          .replace(/^https?:\/\//, '')
          .replace(/^www\./, '')
          .replace(/\/$/, '')
          .split('/')
          .pop()
          ?.replace(/-/g, ' ')
          .replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Unknown';
        
        const newMessage = {
          id: `research-${completion.id}-${Date.now()}`,
          text: `🔬 Research completed for ${urlName} (enhanced to ${completion.new_completeness_score}%)`,
          severity: 'success' as const
        };
        
        setMessages(prev => [...prev, newMessage]);
        
        setTimeout(() => {
          setMessages(prev => prev.filter(msg => msg.id !== newMessage.id));
        }, 6000);
      });
    }
    
    setPreviousResearchCompletions(currentResearchIds);
  }, [status?.recentResearchCompletions, previousResearchCompletions]);

  const getQueueCounts = () => {
    if (!status?.queue) return { V: 0, C: 0, S: 0, other: 0, total: 0 };
    
    const counts = { V: 0, C: 0, S: 0, other: 0, total: 0 };
    
    status.queue.forEach((item: any) => {
      if (item.status === 'pending') {
        const count = parseInt(item.count) || 0;
        counts.total += count;
        
        switch (item.priority) {
          case 'V': counts.V += count; break;
          case 'I': counts.V += count; break; // Map 'I' (Important) to 'V' (Very Important)
          case 'C': counts.C += count; break;
          case 'S': counts.S += count; break;
          default: counts.other += count; break;
        }
      }
    });
    
    return counts;
  };

  const queueCounts = getQueueCounts();
  
  // Get stats from enhanced API response
  const schemaGenStats = status?.stats?.schemaGeneration || { processing: 0, completed: 0, total: 0 };
  const researchStats = status?.stats?.research || { processing: 0, completed: 0, total: 0 };
  const todayCompleted = status?.stats?.todayCompleted || 0;
  const todayResearched = status?.stats?.todayResearched || 0;

  // Check if there are active batches
  const hasActiveGeneration = status?.activeBatches?.some((b: any) => b.status === 'processing' || b.status === 'in_progress');
  const hasActiveResearch = status?.activeResearchBatches?.some((b: any) => b.status === 'processing');
  
  // Get active batch info for display
  const activeBatch = status?.activeBatches?.find((b: any) => b.status === 'processing' || b.status === 'in_progress');

  return (
    <>
    <Paper sx={{ 
      p: 4, 
      mb: 4, 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <Box sx={{ 
        position: 'absolute', 
        top: -50, 
        right: -50, 
        width: 200, 
        height: 200, 
        background: 'rgba(255,255,255,0.1)', 
        borderRadius: '50%' 
      }} />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, position: 'relative', zIndex: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Schema Generation Status
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={autoRefresh ? "Auto-refresh ON" : "Auto-refresh OFF"}
            size="small"
            color={autoRefresh ? "success" : "default"}
            onClick={() => setAutoRefresh(!autoRefresh)}
            sx={{ 
              bgcolor: autoRefresh ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255,255,255,0.2)', 
              color: 'white',
              cursor: 'pointer'
            }}
          />
          {!autoRefresh && (
            <Tooltip title="Refresh Status">
              <IconButton onClick={onRefresh} size="small" sx={{ color: 'white' }}>
                {loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Two Phase Display */}
      <Grid container spacing={4}>
        {/* Phase 1: Schema Generation */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ 
            bgcolor: 'rgba(255,255,255,0.1)', 
            borderRadius: 2, 
            p: 3,
            height: '100%'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CodeIcon />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Phase 1: Schema Generation
              </Typography>
            </Box>
            
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {schemaGenStats.processing}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Processing
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {todayCompleted}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Completed Today
                </Typography>
              </Grid>
            </Grid>
            
            {hasActiveGeneration && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.3)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: 'white'
                    }
                  }} 
                />
                <Typography variant="caption" sx={{ opacity: 0.7, mt: 0.5, display: 'block' }}>
                  Using Claude Batch API (50% cost savings)
                </Typography>
              </Box>
            )}
            
            {/* Detailed batch progress */}
            {status?.batchProgress && status.batchProgress.length > 0 && (
              <Box sx={{ mt: 2 }}>
                {status.batchProgress.map((batch: any) => (
                  <Box key={batch.batchId} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        Batch {batch.batchId.slice(-6)}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 'bold' }}>
                        {batch.completedUrls}/{batch.totalUrls} completed
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate"
                      value={(batch.completedUrls / batch.totalUrls) * 100}
                      sx={{ 
                        height: 6,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: 'white'
                        }
                      }} 
                    />
                  </Box>
                ))}
              </Box>
            )}
            
            {/* Queue breakdown */}
            {queueCounts.total > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Queued: {queueCounts.total} ({queueCounts.V} Very Important, {queueCounts.C} Commercial, {queueCounts.S} Standard)
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>

        {/* Phase 2: Research Enhancement */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ 
            bgcolor: 'rgba(255,255,255,0.1)', 
            borderRadius: 2, 
            p: 3,
            height: '100%'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ResearchIcon />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Phase 2: Research Enhancement
              </Typography>
            </Box>
            
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {researchStats.processing}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Researching
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {todayResearched}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Enhanced Today
                </Typography>
              </Grid>
            </Grid>
            
            {hasActiveResearch && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.3)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: 'white'
                    }
                  }} 
                />
                <Typography variant="caption" sx={{ opacity: 0.7, mt: 0.5, display: 'block' }}>
                  Enhancing with web search data
                </Typography>
              </Box>
            )}
            
            {/* Detailed research batch progress */}
            {status?.researchBatchProgress && status.researchBatchProgress.length > 0 && (
              <Box sx={{ mt: 2 }}>
                {status.researchBatchProgress.map((batch: any) => (
                  <Box key={batch.batchId} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        Research {batch.batchId.slice(-6)}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 'bold' }}>
                        {batch.completedSchemas}/{batch.totalSchemas} enhanced
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate"
                      value={(batch.completedSchemas / batch.totalSchemas) * 100}
                      sx={{ 
                        height: 6,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: 'white'
                        }
                      }} 
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Active Batches Summary */}
      {(status?.activeBatches?.length > 0 || status?.activeResearchBatches?.length > 0) && (
        <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'rgba(255,255,255,0.2)' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            {status?.activeBatches?.map((batch: any, idx: number) => (
              <Chip
                key={batch.batch_id || idx}
                icon={<CodeIcon />}
                label={`Generation: ${batch.url_count} URLs (${batch.status === 'in_progress' ? 'processing' : batch.status})`}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />
            ))}
            {status?.activeResearchBatches?.filter((batch: any) => batch.status === 'processing').map((batch: any, idx: number) => (
              <Chip
                key={batch.batch_id || idx}
                icon={<ResearchIcon />}
                label={`Research: ${batch.schema_count} schemas (${batch.status})`}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Processing Rate */}
      <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'rgba(255,255,255,0.2)', position: 'relative', zIndex: 1 }}>
        <Typography variant="body2" sx={{ opacity: 0.8, textAlign: 'center' }}>
          <MagicIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 1 }} />
          Estimated rate: ~12 schemas/hour with automatic research enhancement
        </Typography>
      </Box>
    </Paper>

    {/* Render multiple snackbars */}
    {messages.map((message, index) => (
      <Snackbar
        key={message.id}
        open={true}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ 
          top: `${24 + index * 80}px !important`,
          position: 'fixed'
        }}
      >
        <Alert 
          severity={message.severity} 
          onClose={() => setMessages(prev => prev.filter(msg => msg.id !== message.id))}
          sx={{ 
            width: '100%',
            boxShadow: 3,
            '& .MuiAlert-message': {
              fontSize: '1rem',
              fontWeight: 500
            }
          }}
        >
          {message.text}
        </Alert>
      </Snackbar>
    ))}

    </>
  );
};

export default QueueStatus;